import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Alignment tab — the Objective → Key Result → Goal tree.
// Read for everyone; create / edit / link affordances appear where the
// caller has manage power (objective.can_edit, set server-side).
import { notify } from "../Toast";
import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { HealthPill, ProgressBar, SOURCE_LABEL } from "./okrShared";
import { KeyResultForm, ObjectiveForm } from "./OkrForms";
function fmtValue(kr) {
    const cur = Math.round(kr.current_value * 10) / 10;
    const tgt = kr.target_value;
    const unit = kr.unit ? ` ${kr.unit}` : "";
    if (kr.progress_source === "rollup_goals")
        return `${kr.pct}% · ${kr.goal_count} goals`;
    if (tgt == null)
        return `${cur}${unit}`;
    return `${cur} / ${tgt}${unit}`;
}
export default function AlignmentTab({ user, units, }) {
    const [objectives, setObjectives] = useState([]);
    const [myGoals, setMyGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [objForm, setObjForm] = useState(null);
    const [krForm, setKrForm] = useState(null);
    const [linkFor, setLinkFor] = useState(null); // kr id with open picker
    const manageableBuIds = useMemo(() => (user.role === "admin" ? units.map((u) => u.id) : user.business_unit_ids ?? []), [user, units]);
    const canCreate = user.role !== "worker" && (user.role === "admin" || manageableBuIds.length > 0);
    async function load() {
        setLoading(true);
        setError(null);
        try {
            const [res, goals] = await Promise.all([
                api.get("/okr/objectives"),
                api.get("/me/goals"),
            ]);
            setObjectives(res.objectives);
            setMyGoals(goals);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't load objectives");
        }
        finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
    }, []);
    async function deleteObjective(o) {
        if (!confirm(`Delete "${o.title}" and its key results? This can't be undone.`))
            return;
        try {
            await api.del(`/okr/objectives/${o.id}`);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't delete");
        }
    }
    async function deleteKr(kr) {
        if (!confirm(`Delete key result "${kr.title}"?`))
            return;
        try {
            await api.del(`/okr/key-results/${kr.id}`);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't delete");
        }
    }
    async function linkGoal(krId, goalId) {
        try {
            await api.put(`/okr/key-results/${krId}/goals/${goalId}`);
            setLinkFor(null);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't link goal");
        }
    }
    async function unlinkGoal(krId, goalId) {
        try {
            await api.del(`/okr/key-results/${krId}/goals/${goalId}`);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't unlink goal");
        }
    }
    if (loading)
        return _jsx("div", { className: "muted", children: "Loading objectives\u2026" });
    if (error)
        return _jsx("div", { className: "error", children: error });
    return (_jsxs("div", { className: "okr-align", children: [_jsxs("div", { className: "okr-align-head", children: [_jsx("p", { className: "muted small okr-align-caption", children: "Company and business-unit objectives, the key results under them, and the goals laddering up. A key result's progress can be entered by hand, rolled up from its linked goals, or auto-tracked from completed projects / logged time." }), canCreate && (_jsx("button", { className: "btn-primary", onClick: () => setObjForm("new"), children: "+ New objective" }))] }), objectives.length === 0 && (_jsx("div", { className: "goals-empty", children: _jsxs("p", { className: "muted", children: ["No objectives yet.", " ", canCreate
                            ? "Create one and hang key results under it."
                            : "Your admin or manager hasn't set any yet."] }) })), objectives.map((o) => (_jsxs("div", { className: "okr-objective", children: [_jsxs("div", { className: "okr-objective-top", children: [_jsxs("div", { className: "okr-objective-meta", children: [_jsxs("div", { className: "okr-lvl", children: [o.business_unit_name ? `${o.business_unit_name} objective` : "Company objective", o.period ? ` · ${o.period}` : ""] }), _jsx("h3", { children: o.title }), o.description && _jsx("div", { className: "muted small", children: o.description }), _jsxs("div", { className: "muted small", children: [o.owner_name ? `Owner: ${o.owner_name} · ` : "", o.key_results.length, " key result", o.key_results.length === 1 ? "" : "s"] })] }), _jsxs("div", { className: "okr-objective-score", children: [_jsxs("div", { className: "okr-big", children: [o.pct, "%"] }), _jsx(HealthPill, { health: o.health })] })] }), _jsx(ProgressBar, { pct: o.pct, health: o.health }), o.can_edit && (_jsxs("div", { className: "okr-row-actions", children: [_jsx("button", { onClick: () => setKrForm({ objectiveId: o.id }), children: "+ Key result" }), _jsx("button", { onClick: () => setObjForm(o), children: "Edit" }), _jsx("button", { className: "link-danger", onClick: () => deleteObjective(o), children: "Delete" })] })), _jsx("div", { className: "okr-krs", children: o.key_results.map((kr) => {
                            const linkable = myGoals.filter((g) => !kr.linked_goals.some((lg) => lg.id === g.id));
                            return (_jsx("div", { className: "okr-kr", children: _jsxs("div", { className: "okr-kr-card", children: [_jsxs("div", { className: "okr-kr-row", children: [_jsxs("div", { children: [_jsx("div", { className: "okr-kr-title", children: kr.title }), _jsxs("div", { className: "okr-kr-owner", children: [kr.business_unit_name && (_jsx("span", { className: "okr-tag", children: kr.business_unit_name })), _jsx("span", { className: "muted small", children: SOURCE_LABEL[kr.progress_source] })] })] }), _jsxs("div", { className: "okr-kr-score", children: [_jsx("div", { className: "okr-kr-num", children: fmtValue(kr) }), _jsx(HealthPill, { health: kr.health })] })] }), _jsx(ProgressBar, { pct: kr.pct, health: kr.health }), kr.linked_goals.length > 0 && (_jsx("div", { className: "okr-goals", children: kr.linked_goals.map((g) => (_jsxs("div", { className: "okr-goal", children: [_jsxs("span", { className: "muted small", children: [g.owner_name ?? "—", " \u00B7 ", g.title] }), canCreate && (o.can_edit || g.owner_id === user.id) && (_jsx("button", { className: "okr-unlink", title: "Unlink this goal", onClick: () => unlinkGoal(kr.id, g.id), children: "\u00D7" }))] }, g.id))) })), _jsxs("div", { className: "okr-kr-actions", children: [canCreate &&
                                                    (linkFor === kr.id ? (_jsxs("span", { className: "okr-link-picker", children: [_jsxs("select", { defaultValue: "", onChange: (e) => e.target.value && linkGoal(kr.id, e.target.value), children: [_jsx("option", { value: "", disabled: true, children: linkable.length ? "Pick a goal to ladder up…" : "No goals to link" }), linkable.map((g) => (_jsx("option", { value: g.id, children: g.title }, g.id)))] }), _jsx("button", { onClick: () => setLinkFor(null), children: "Cancel" })] })) : (_jsx("button", { onClick: () => setLinkFor(kr.id), children: "+ Link a goal" }))), o.can_edit && (_jsxs(_Fragment, { children: [_jsx("button", { onClick: () => setKrForm(kr), children: "Edit" }), _jsx("button", { className: "link-danger", onClick: () => deleteKr(kr), children: "Delete" })] }))] })] }) }, kr.id));
                        }) })] }, o.id))), objForm && (_jsx(ObjectiveForm, { initial: objForm === "new" ? null : objForm, units: units, manageableBuIds: manageableBuIds, isAdmin: user.role === "admin", onClose: () => setObjForm(null), onSaved: async () => {
                    setObjForm(null);
                    await load();
                } })), krForm && (_jsx(KeyResultForm, { objectiveId: "objectiveId" in krForm ? krForm.objectiveId : krForm.objective_id, initial: "objectiveId" in krForm ? null : krForm, units: units, onClose: () => setKrForm(null), onSaved: async () => {
                    setKrForm(null);
                    await load();
                } }))] }));
}
